import Image from 'next/image';
import React, { useState } from 'react';
import { ISingleMediaFile } from 'types/cms';

import { getImageUrl } from './getImageUrl';

export type ImageFormat = 'small' | 'medium' | 'large' | 'thumbnail';

interface CmsImageProps {
  imageProps: Pick<ISingleMediaFile, 'formats' | 'url'>;
  format: ImageFormat;
  quality?: number;
  altText: string;
  layout?: 'fixed' | 'fill' | 'intrinsic' | 'responsive';
  height?: number;
  width?: number;
  objectFit?: 'cover' | 'contain' | 'scale-down' | 'fill' | 'none';
  className?: string;
  priority?: boolean;
  sizes?: string;
}

const CmsImage = ({
  imageProps,
  altText,
  format,
  quality,
  layout,
  height,
  width,
  objectFit,
  priority,
  className,
  sizes,
}: CmsImageProps) => {
  // Tracks the specific URL that failed to load (rather than a plain boolean) so a component
  // instance reused for a different image - e.g. a swapped CMS entry without a full remount -
  // retries the new URL instead of staying hidden forever.
  const [erroredUrl, setErroredUrl] = useState<string | null>(null);
  const imageUrl = getImageUrl(imageProps.formats, format, imageProps.url);
  // `layout`/`objectFit` are the pre-Next-13 next/image API - translate them to `fill` +
  // inline `objectFit` (Next logs a "legacy prop" warning otherwise). `responsive` needs an
  // explicit `width: 100%` (matching Next's own legacy-prop translation) so the image still
  // fills a column narrower than its intrinsic width; `intrinsic` doesn't set width so it can
  // shrink below its intrinsic size but never grow past it.
  const fill = layout === 'fill';
  const responsive = layout === 'responsive';

  const imageStyle = {
    ...(fill
      ? { maxWidth: '100%' }
      : responsive
      ? { width: '100%', height: 'auto' }
      : { maxWidth: '100%', height: 'auto' }),
    ...(objectFit ? { objectFit } : {}),
  };

  // Falls back to rendering nothing - rather than the browser's broken-image icon/alt text
  // bleeding through a container styled to expect a photo - when the resolved URL fails to
  // actually load (e.g. a deleted/renamed CMS asset), same as when there's no image at all.
  if (!imageUrl || imageUrl === erroredUrl) {
    return null;
  }

  return (
    <Image
      className={className}
      alt={altText}
      src={imageUrl}
      priority={priority}
      fill={fill}
      quality={quality}
      height={fill ? undefined : height}
      width={fill ? undefined : width}
      sizes={sizes}
      style={imageStyle}
      onError={() => setErroredUrl(imageUrl)}
    />
  );
};

export default CmsImage;
